import { newRef } from "./ref.js";
import {
  AREA_5XX,
  EXPECTED_STATE_CODES,
  GATING_CODES,
  SERVER_REPORTED_CODES,
  SILENT_PATHS,
} from "./catalogue.js";
import { describeOperation } from "./describeOperation.js";
import { looksTechnical, safeText } from "./sanitise.js";
import { isPageInterrupted, isPageUnloading } from "./pageLifecycle.js";

/**
 * Turns any axios error into what the user should be told, plus what
 * Monitoring should record. Pure: no toasts, no reporting, no side effects.
 *
 *   { code, message, ref, status, silent, requestUrl, rawMessage }
 *
 * `message` is always safe to render. `rawMessage` is the unsanitised backend
 * text and is for the Monitoring record ONLY — rendering it is the bug this
 * whole module exists to fix.
 */

const urlOf = (error) => error?.config?.url || error?.response?.config?.url || "";

const humaniseField = (field) =>
  field
    .replace(/_/g, " ")
    .replace(/\./g, " → ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

/**
 * Laravel's 422 field messages are written for users ("The email field is
 * required when phone is not present"), so they are kept rather than replaced
 * with something blander. Each line is still sanitised — a validation rule can
 * quote a database error — and if every line is dropped we fall back.
 */
const validationMessage = (data, fallback) => {
  const errors = data?.errors;

  if (!errors || typeof errors !== "object") {
    return safeText(data?.message, fallback);
  }

  const lines = Object.entries(errors)
    .flatMap(([field, messages]) =>
      (Array.isArray(messages) ? messages : [messages]).map((message) =>
        looksTechnical(message) ? null : `${humaniseField(field)}: ${message}`
      )
    )
    .filter(Boolean);

  return lines.length ? lines.join("\n") : fallback;
};

// axios 0.19 rejects a cancelled request with a `Cancel` object (`__CANCEL__`);
// axios ≥ 0.22 uses a CanceledError with code ERR_CANCELED. Both mean the app
// chose to drop the request — never an outage.
const isCancelled = (error) =>
  Boolean(error?.__CANCEL__) ||
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError";

const SAFE_METHODS = ["get", "head", "options"];

/**
 * A no-response failure the browser caused, not the network or our API: the
 * page was unloading, hidden, or mid-transition (see pageLifecycle.js). Reads
 * are dropped whenever the page is interrupted — nobody asked for them and they
 * are re-fetched on return. A mutation is only dropped once the page is
 * actually unloading: on a hidden tab the user still needs to hear that their
 * save did not land.
 */
const abortedByBrowser = (method) =>
  isPageUnloading() || (SAFE_METHODS.includes(method || "get") && isPageInterrupted());

export function classifyApiError(error, { fallback, preferServerMessage = false } = {}) {
  const requestUrl = urlOf(error);
  const method = (error?.config?.method ?? "").toLowerCase();
  const status = error?.response?.status ?? null;
  const data = error?.response?.data ?? {};
  const rawMessage =
    (data && typeof data === "object"
      ? data.message || data.error || data.exception
      : null) ||
    error?.message ||
    null;

  const silent =
    Boolean(error?.config?.metadata?.silent) ||
    SILENT_PATHS.some((path) => path.test(requestUrl)) ||
    // Plan-gating 403s are announced by EntitlementErrorListener, which has
    // the server's own upgrade copy. Without this they toast twice.
    GATING_CODES.includes(data?.code) ||
    // A 503 outage the server has already put on the board itself.
    (status === 503 && SERVER_REPORTED_CODES.includes(data?.code)) ||
    // An expected state the requesting component explains itself (a lapsed
    // Google Calendar connection) — not a fault, so not a report.
    (status === 409 && EXPECTED_STATE_CODES.includes(data?.code)) ||
    // Cancelled by the app, or killed by the browser as the tab hid/unloaded.
    // Safari reports the latter as a plain "Network Error" with no status —
    // Monitoring group #1177 — so without this it reads as an outage.
    (!status && (isCancelled(error) || abortedByBrowser(method)));

  const described = describeOperation(method, requestUrl);
  // A caller can say what the user was doing when the URL doesn't make it
  // obvious ("start the eBay sign-in"). Their phrase wins.
  const phrase = fallback || described.phrase;
  const gerund = fallback ? "That" : described.gerund;
  const noun = described.noun;

  const generic = `We couldn't ${phrase} — something failed on our side. We've been notified.`;
  const base = { ref: newRef(), status, silent, requestUrl, rawMessage };

  if (!status) {
    if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message ?? "")) {
      return {
        ...base,
        code: "TIMEOUT",
        message: `${gerund} ${phrase.replace(/^\w+\s/, "")} is taking longer than expected. Try again in a moment.`,
      };
    }

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return {
        ...base,
        code: "OFFLINE",
        message: `You're offline. We'll need a connection to ${phrase}.`,
      };
    }

    return {
      ...base,
      code: "UNREACHABLE",
      message: `We couldn't reach Retail Circle to ${phrase}. Check your connection and try again.`,
    };
  }

  // The web app is staff-only: the backend ends a client's session with this
  // code. It is not an expired session, so don't say it is.
  if (status === 401 && data.code === "rc_web_staff_only") {
    return {
      ...base,
      code: "STAFF_ONLY",
      message: "This account can't sign in to Retail Circle. Ask your store admin to invite you to their team.",
    };
  }

  if (status === 401) {
    return {
      ...base,
      code: "SESSION_EXPIRED",
      message: "Your session has expired. Please log in again.",
    };
  }

  if (status === 403) {
    // A blanket "you don't have permission" actively misleads on the one 403 a
    // normal visitor can hit: POST /api/tokens/create returns
    // {"error": "Access denied for this platform."} when the account lacks
    // access_rc_web, which reads as a glitch rather than "this account cannot
    // sign in here". The server's reason wins when it survives the sanitiser.
    return {
      ...base,
      code: "FORBIDDEN",
      message: safeText(
        data?.error || data?.message,
        `You don't have permission to ${phrase}.`
      ),
    };
  }

  if (status === 404) {
    return {
      ...base,
      code: "NOT_FOUND",
      message: `That ${noun} no longer exists. It may have been deleted.`,
    };
  }

  if (status === 409) {
    return {
      ...base,
      code: "CONFLICT",
      message: `Someone else changed this ${noun} while you were editing. Reload and try again.`,
    };
  }

  if (status === 413) {
    return { ...base, code: "TOO_LARGE", message: "That file is too large to upload." };
  }

  if (status === 422) {
    return {
      ...base,
      code: "VALIDATION",
      message: validationMessage(data, "Please check the details and try again."),
    };
  }

  if (status === 429) {
    return {
      ...base,
      code: "RATE_LIMITED",
      message: "Too many requests. Wait a moment and try again.",
    };
  }

  if (status === 402 || /\/api\/(payment|deposit)/.test(requestUrl)) {
    return {
      ...base,
      code: "PAYMENT_FAILED",
      message: "The payment couldn't be processed. Check the details and try again.",
    };
  }

  if (status >= 500) {
    const area = AREA_5XX.find((entry) => entry.match.test(requestUrl));
    const defaultMessage = area ? area.message : generic;

    return {
      ...base,
      code: area ? area.code : "SERVER_ERROR",
      // Only when the caller opted in: a few endpoints answer with a reason
      // written for merchants ("Xero rejected this: account code 200 is not
      // mapped"), which no status code can reconstruct. Sanitised like
      // everything else, so a stack trace still never gets through.
      message: preferServerMessage
        ? safeText(rawMessage, defaultMessage)
        : defaultMessage,
    };
  }

  return { ...base, code: "UNKNOWN", message: generic };
}
